import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useUserStore } from '@/stores/userStore';
import { Button } from '../ui/Button';
import { Loader2, UserPlus, Shield, ShieldCheck, Trash2, AlertTriangle } from 'lucide-react';
import { useToastStore } from '@/stores/toastStore';

interface UserGateProps {
  children: ReactNode;
}

export const UserGate: React.FC<UserGateProps> = ({ children }) => {
  const {
    users,
    activeUserId,
    currentUser,
    lastActiveUserId,
    isLoading,
    error,
    loadUsers,
    selectUser,
    createUser,
    deleteUser,
    updateUserRole
  } = useUserStore();
  const { addToast } = useToastStore();
  const [newUserName, setNewUserName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createAsAdmin, setCreateAsAdmin] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<typeof users[number] | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [promotingUserId, setPromotingUserId] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const hasAdminPrivileges = useMemo(() => {
    if (!activeUserId) {
      // Logged-out state defaults to the bootstrap admin ("default-user")
      return true;
    }
    return currentUser?.role === 'admin';
  }, [activeUserId, currentUser]);

  const handlePromote = async (userId: string) => {
    setPromotingUserId(userId);
    try {
      await updateUserRole(userId, 'admin');
      addToast('success', 'User elevated to admin.');
    } catch (err) {
      console.error('[UserGate] Failed to promote user', err);
      addToast('error', 'Failed to update user role.');
    } finally {
      setPromotingUserId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteUser(deleteTarget.id);
      addToast('success', `${deleteTarget.displayName} removed.`);
      setDeleteTarget(null);
    } catch (err) {
      console.error('[UserGate] Failed to delete user', err);
      addToast('error', 'Failed to delete user.');
    } finally {
      setDeleting(false);
    }
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-3 bg-background">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading users…</p>
      </div>
    );
  }

  if (!activeUserId) {
    return (
      <div className="min-h-screen bg-background flex justify-center">
        <div className="w-[80vw] py-10 space-y-6">
          <div>
            <h2 className="text-xl font-semibold">Select a User</h2>
            <p className="text-sm text-muted-foreground">
              Choose an existing user or create a new profile to continue.
            </p>
            {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
            {hasAdminPrivileges ? (
              <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Admin mode: you can promote or delete users below.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Shield className="w-3 h-3" /> Need admin access? Sign in as a user and use the account menu to elevate yourself.
              </p>
            )}
          </div>

          <div className="grid grid-cols-5 gap-3">
            {users.length === 0 ? (
              <p className="col-span-5 text-sm text-muted-foreground">No users yet. Create one below.</p>
            ) : (
              users.map((user) => {
                const canManageUser = hasAdminPrivileges && user.id !== activeUserId;
                const isLastActive = !activeUserId && lastActiveUserId === user.id;
                return (
                  <div
                    key={user.id}
                    className="border border-border rounded-md p-3 flex flex-col gap-2 bg-card/60 hover:bg-card transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1">
                        <div className="text-sm font-semibold">{user.displayName}</div>
                        <div className="text-xs text-muted-foreground">{user.email || user.id}</div>
                        {isLastActive && (
                          <div className="text-[11px] text-primary font-medium">Last signed in</div>
                        )}
                      </div>
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded-full border ${
                          user.role === 'admin'
                            ? 'border-emerald-500 text-emerald-600'
                            : 'border-border text-muted-foreground'
                        }`}
                      >
                        {user.role}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="xs"
                        className="flex-1 min-w-[90px] justify-center"
                        onClick={() => selectUser(user.id)}
                      >
                        Sign in
                      </Button>
                      {canManageUser && user.role !== 'admin' ? (
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          className="flex-1 min-w-[90px] justify-center"
                          disabled={promotingUserId === user.id}
                          onClick={() => handlePromote(user.id)}
                        >
                          {promotingUserId === user.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Make admin'
                          )}
                        </Button>
                      ) : (
                        <span className="flex-1 min-w-[90px]" />
                      )}
                      {canManageUser && (
                        <Button
                          type="button"
                          size="xs"
                          variant="ghost"
                          className="flex-1 min-w-[90px] justify-center text-red-500 hover:bg-red-500/10"
                          onClick={() => setDeleteTarget(user)}
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!newUserName.trim()) return;
              try {
                setCreating(true);
                const user = await createUser(newUserName.trim(), {
                  makeAdmin: createAsAdmin && hasAdminPrivileges
                });
                if (user) {
                  selectUser(user.id);
                }
                setNewUserName('');
                setCreateAsAdmin(false);
              } finally {
                setCreating(false);
              }
            }}
            className="space-y-2 border-t border-border pt-4"
          >
            <label className="text-sm font-medium flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-muted-foreground" />
              Create New User
            </label>
            <input
              type="text"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              placeholder="Display name"
              className="w-full border border-border rounded px-3 py-2 text-sm bg-background"
            />
            <label className={`flex items-center gap-2 text-xs ${hasAdminPrivileges ? 'text-muted-foreground' : 'text-muted-foreground/60'}`}>
              <input
                type="checkbox"
                checked={createAsAdmin}
                onChange={(e) => setCreateAsAdmin(e.target.checked)}
                disabled={!hasAdminPrivileges}
              />
              Start this user with admin access
            </label>
            <Button type="submit" className="w-full" disabled={creating || !newUserName.trim()}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create & Continue'}
            </Button>
          </form>
        </div>
        {deleteTarget && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4">
            <div className="w-full max-w-md bg-background border border-border rounded-lg shadow-xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
                <div>
                  <h3 className="font-semibold">Remove {deleteTarget.displayName}?</h3>
                  <p className="text-sm text-muted-foreground">
                    This hides their account and prevents future logins. Projects remain for auditing.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-red-500 hover:bg-red-500/90"
                  onClick={handleDeleteUser}
                  disabled={deleting}
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return <>{children}</>;
};
